import Picture, { ImageOverlay } from './Picture';
import styled, { css } from 'styled-components';
import { PlaceholderText } from './Placeholders';
import SemanticList from './SemanticList';
import Stack from './Stack';

function PlaceholderParagraph({
    lines = 2,
    shimmerWidth = '2000px',
    ...props
}) {
    return (
        <PlaceholderText lines={lines} shimmerWidth={shimmerWidth} {...props} />
    );
}

const minRowHeightByBreakpoint = {
    $minRowHeightXs: '80px',
    $minRowHeightSm: '100px',
    $minRowHeightMd: '120px',
    $minRowHeightLg: '160px',
};

const SpotlightCardCSS = css`
    flex-grow: 1;
    overflow: hidden;
    display: grid;
    grid-template-columns: ${({ $minRowHeightXs }) => $minRowHeightXs} 1fr;
    align-items: stretch;
    background-color: ${({ theme }) => theme.gray_7};
    border-color: ${({ theme }) => theme.gray_5};
    border-width: 1px;
    border-style: solid;
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontSemiBold};
    && {
        text-decoration: none;
    }

    ${({ isLoading }) => (isLoading ? `cursor: wait;` : null)}
    ${({ isDisabled }) => (isDisabled ? `opacity: .5;` : null)}

    &:focus-within, &:hover {
        background-color: ${({ theme }) => theme.purple_5};
        border-color: ${({ theme }) => theme.purple_4};
    }
`;

const SpotlightCard = styled.div.attrs((props) => ({
    ...minRowHeightByBreakpoint,
    ...props,
}))`
    ${SpotlightCardCSS}

    @media (min-width: ${({ theme }) => theme.bp.sm}px) {
        grid-template-columns: ${({ $minRowHeightSm }) => $minRowHeightSm} 1fr;
    }

    @media (min-width: ${({ theme }) => theme.bp.md}px) {
        grid-template-columns: ${({ $minRowHeightMd }) => $minRowHeightMd} 1fr;
    }

    @media (min-width: ${({ theme }) => theme.bp.lg}px) {
        grid-template-columns: ${({ $minRowHeightLg }) => $minRowHeightLg} 1fr;
    }
`;

const MiniCard = styled.div`
    ${SpotlightCardCSS}
    grid-template-columns: 60px 1fr;
    min-height: 60px;
`;

const ContentStack = styled(Stack).attrs(
    ({ $space = (theme) => `${theme.space_5}px` }) => ({
        $space,
    }),
)`
    align-self: center;
    padding: ${({ theme }) => `${theme.space_7}px ${theme.space_8}px`};

    ${SpotlightCard} & {
        @media (min-width: ${({ theme }) => theme.bp.md}px) {
            padding: ${({ theme }) => `${theme.space_8}px`};
        }

        @media (min-width: ${({ theme }) => theme.bp.lg}px) {
            padding: ${({ theme }) =>
                `${theme.space_10}px ${theme.space_11}px`};
        }
    }

    ${MiniCard} & {
        padding: ${({ theme }) => `0 ${theme.space_8}px`};
    }
`;

const HeadingText = styled.div`
    font-weight: ${({ theme }) => theme.fontBold};
    font-size: 1.125em;
    color: ${({ theme }) => theme.gray_1};
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const SubHeadingText = styled.div`
    color: ${({ theme }) => theme.gray_3};
`;

const ClassificationText = styled.div`
    color: ${({ theme }) => theme.purple_1};
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;

    ${MiniCard} & {
        -webkit-line-clamp: 2;
    }
`;

export function ImageTile({
    children,
    image,
    alt = '',
    $position,
    Wrapper = SpotlightCard,
    ...props
}) {
    return (
        <Wrapper {...props}>
            <Picture
                src={image}
                alt={alt}
                isLoading={!!props.isLoading}
                isDisabled={!!props.isDisabled}
                $position={$position}
            />
            {children}
        </Wrapper>
    );
}

function Tile({ classification, heading, subheading, ...props }) {
    return (
        <ImageTile {...props}>
            <ContentStack>
                {props.isLoading && !classification ? (
                    <PlaceholderText width="6rem" />
                ) : classification ? (
                    <ClassificationText>{classification}</ClassificationText>
                ) : null}
                {props.isLoading && !heading ? (
                    <PlaceholderParagraph />
                ) : heading ? (
                    <HeadingText>{heading}</HeadingText>
                ) : null}
                {props.isLoading && !subheading ? (
                    <PlaceholderText width="4rem" />
                ) : subheading ? (
                    <SubHeadingText>{subheading}</SubHeadingText>
                ) : null}
            </ContentStack>
        </ImageTile>
    );
}

function MiniTile({ Wrapper = MiniCard, ...props }) {
    return <Tile Wrapper={Wrapper} {...props} />;
}

const TileGrid = styled.div.attrs(({ as = SemanticList, ...props }) => ({
    as,
    ...minRowHeightByBreakpoint,
    ...props,
}))`
    display: grid;
    grid-row-gap: ${({ theme }) => theme.space_8}px;
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(
        ${({ $minRowHeightXs }) => $minRowHeightXs},
        max-content
    );
    font-size: ${({ theme }) => theme.space_5}px;
    color: ${({ theme }) => theme.gray_1};

    @media (min-width: ${({ theme }) => theme.bp.sm}px) {
        grid-column-gap: ${({ theme }) => theme.space_10}px;
        grid-row-gap: ${({ theme }) => theme.space_11}px;
        grid-template-columns: repeat(2, 1fr);
        grid-auto-rows: minmax(
            ${({ $minRowHeightSm }) => $minRowHeightSm},
            max-content
        );
    }

    @media (min-width: ${({ theme }) => theme.bp.md}px) {
        grid-auto-rows: minmax(
            ${({ $minRowHeightMd }) => $minRowHeightMd},
            max-content
        );
    }

    @media (min-width: ${({ theme }) => theme.bp.lg}px) {
        grid-auto-rows: minmax(
            ${({ $minRowHeightLg }) => $minRowHeightLg},
            max-content
        );
        font-size: ${({ theme }) => theme.space_7}px;
    }

    &:hover
        > :hover
        ${ImageOverlay},
        &:focus-within
        > :focus-within
        ${ImageOverlay} {
        background-image: linear-gradient(
            0deg,
            rgba(152, 100, 255, 0.15),
            rgba(152, 100, 255, 0.15)
        );
    }

    & > li {
        display: flex;
        align-items: stretch;
    }
`;

const Spotlight = { Tile, TileGrid, MiniTile };

export default Spotlight;
